<template>
  <Layout
    :title="computedTitle"
    max-width="100%"
  >
    <template #addOnTitle>
      <span
        class="tag"
        :class="proposalStatusCypher[proposalDetails.status]?.tag"
      >
        {{ proposalStatusCypher[proposalDetails.status]?.translation || proposalDetails?.status }}
      </span>
    </template>
    <template #actions>
      <button
        class="button"
        @click="goBack"
      >
        Voltar
      </button>
    </template>

    <div class="proposal-details">
      <section
        class="first-column"
        :proposal-status="proposalDetails.status"
        :has-installments="!!installmentList.length"
      >
        <!-- User Informations -->
        <div class="is-card proposal-details__vehicle-data">
          <h2 class="is-bolder font-md">
            {{ proposalDetails.name }}
          </h2>

          <BaseVerticalTable
            no-border
            :show-header="false"
            :headers="proposalDetailsTableHeaders"
            :data="proposalDetails"
          >
            <template #external_proposal_number="{ value, row }">
              <span
                class="ccb__proposal_number"
                :class="{
                  no_allowed_status: noStatusToShowCCBModal.includes(proposalDetails.status),
                }"
                @click="openModal('ccbDetails', proposalDetails)"
              >
                {{
                  isSupplierBV(row.supplier_id)
                    ? 'Emitida (Cliente)'
                    : value
                }}
              </span>
            </template>
          </BaseVerticalTable>
        </div>
        <!-- Proposal Details Card -->
        <div class="is-card proposal-details__details-data">
          <div class="details-data__header">
            <h2 class="is-bolder font-md">
              Dados da Proposta
            </h2>
            <div class="details-data__header-actions">
              <button
                class="button"
                @click.prevent="modal.proposalDetails = true"
              >
                <LinkIcon class="action__icon not-stroke" />
              </button>
              <button
                class="button"
                @click.prevent="modal.documentHistory = true"
              >
                <DocumentIcon class="action__icon not-stroke" />
              </button>
              <button
                v-if="(proposalDetails.status !== 'Paga' || isInternalUser)"
                class="button"
                @click.prevent="modal.antiFraud = true"
              >
                <ShieldIcon class="action__icon not-stroke" />
              </button>
            </div>
          </div>

          <BaseVerticalTable
            no-border
            :show-header="false"
            :headers="proposalDataTableHeaders"
            :data="proposalDetails"
          >
            <template #amount="{ value }">
              {{ value | money }}
            </template>
            <template #installments="{ row }">
              {{ row.installments }}x
              {{ row.installment_value || 0 | money }}
            </template>
          </BaseVerticalTable>

          <button
            class="button is-success show__all__details__button"
            @click="goToUpdateProposalPage"
          >
            Ver todos os dados da proposta
          </button>
        </div>
      </section>

      <!-- Installments Table -->
      <div
        v-if="proposalDetails.status === 'Paga' && !!installmentList.length"
        class="is-card proposal-details__installments-data"
      >
        <h2 class="is-bolder font-md">
          Parcelas
        </h2>

        <table
          v-if="isMobile"
          class="table"
        >
          <tr
            v-for="(installment, index) in installmentList"
            :key="index"
          >
            <div>
              <th>Parcela</th>
              <td>
                {{ installment.number }}
              </td>
            </div>
            <div>
              <th>Valor</th>
              <td>
                {{ installment.value | money }}
              </td>
            </div>
            <div>
              <th>Data de Vencimento</th>
              <td>
                {{ installment.due_date.split('-').reverse().join('/') }}
              </td>
            </div>
            <div>
              <td>
                <span
                  class="tag"
                  :class="installmentStatusCypher[installment?.status]?.tag"
                >
                  {{ installment.status }}
                </span>
              </td>
              <td class="installments-data__actions">
                <template v-if="!['Aguardando emissão', 'Cancelada'].includes(installment?.status)">
                  <button
                    v-if="installment?.status === 'Em aberto'"
                    class="button"
                  >
                    Baixar boleto
                  </button>
                  <button
                    class="button icon-button"
                    @click="openInstallmentDetailModal(installment)"
                  >
                    <LinkIcon class="action__icon" />
                  </button>
                </template>
              </td>
            </div>
          </tr>
        </table>
        <table
          v-else
          class="table"
        >
          <thead>
            <tr>
              <th class="font-sm is-semi-bold left-aligned">
                Parcela
              </th>
              <th class="font-sm is-semi-bold left-aligned">
                Valor
              </th>
              <th class="font-sm is-semi-bold left-aligned">
                Data de Venc.
              </th>
              <th class="font-sm is-semi-bold">
                Status
              </th>
              <th class="font-sm is-semi-bold">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(installment, index) in installmentList"
              :key="index"
            >
              <td>
                {{ installment.number }}
              </td>
              <td>
                {{ installment.value | money }}
              </td>
              <td>
                {{ installment.due_date.split('-').reverse().join('/') }}
              </td>
              <td>
                <span
                  class="tag"
                  :class="installmentStatusCypher[installment?.status]?.tag"
                >
                  {{ installment.status }}
                </span>
              </td>
              <td class="installments-data__actions">
                <template v-if="!['Aguardando emissão', 'Cancelada'].includes(installment?.status)">
                  <button
                    v-if="installment?.status === 'Em aberto'"
                    class="button"
                  >
                    Baixar boleto
                  </button>
                  <button
                    class="button icon-button"
                    @click="openInstallmentDetailModal(installment)"
                  >
                    <LinkIcon class="action__icon" />
                  </button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Parcel Details Modal -->
      <ModalAntifraud
        v-if="modal.antiFraud"
        @close="modal.antiFraud = false"
      />
      <!-- Proposal Details Modal -->
      <ProposalDetailsModal
        v-if="modal.proposalDetails"
        :proposal="proposalDetails"
        @close="modal.proposalDetails = false"
      />
      <!-- Document History Modal -->
      <DocumentHistoryModal
        v-if="modal.documentHistory"
        :proposal="proposalDetails"
        @close="modal.documentHistory = false"
      />
      <!-- Installment Detail Modal -->
      <InstallmentDetailModal
        v-if="modal.installmentDetail"
        :proposal="proposalDetails"
        :installment="selectedInstallment"
        @close="modal.installmentDetail = false"
      />
      <!-- CCB Details Modal -->
      <CCBDetailsModal
        v-if="modal.ccbDetails"
        :proposal="proposalDetails"
        @close="modal.ccbDetails = false"
      />
    </div>
  </Layout>
</template>

<script>
/** Core */
import { mapState, mapGetters, mapActions } from 'vuex';

/** Mixins */
import loadingMixin from '@/mixins/loadingView.mixin';

/** Mocks */
import { cypher as proposalStatusCypher } from '@/data/proposalStatusCypher.js';

/** Utils */
import seo from '@/utils/seo';
import { toast } from 'bulma-toast';

/** Data */
import config from '@/data/config';

/** Components */
import Layout from '@/layouts/Layout.vue';

/** Services */
import {
  cgv as CGVAPI,
} from '@/services';

export default {
  name: 'ProposalDetails',
  components: {
    Layout,
    LinkIcon: () => import('../assets/icons/Link.vue'),
    ShieldIcon: () => import('../assets/icons/Shield.vue'),
    DocumentIcon: () => import('../assets/icons/Document.vue'),
    CCBDetailsModal: () => import('@/components/CGV/CCBDetailsModal.vue'),
    ModalAntifraud: () => import('@/components/AntifraudModal/Index.vue'),
    BaseVerticalTable: () => import('@/components/Base/VerticalTable/index.vue'),
    ProposalDetailsModal: () => import('@/components/CGV/ProposalDetailsModal.vue'),
    DocumentHistoryModal: () => import('@/components/CGV/DocumentHistoryModal.vue'),
    InstallmentDetailModal: () => import('@/components/CGV/InstallmentDetailModal.vue'),
  },
  mixins: [loadingMixin],
  data() {
    return {
      proposalDetails: {},
      selectedInstallment: {},
      installmentList: [],
      meta: {
        title: 'Detalhes da Proposta',
      },
      installmentStatusCypher: {
        'Aguardando emissão': {
          tag: 'is-hold',
        },
        'Em aberto': {
          tag: 'is-info',
        },
        'Em atraso': {
          tag: 'is-warning',
        },
        Paga: {
          tag: 'is-success',
        },
        Cancelada: {
          tag: 'is-danger',
        },
      },
      proposalStatusCypher,
      modal: {
        antiFraud: false,
        proposalDetails: false,
        documentHistory: false,
        installmentDetail: false,
        ccbDetails: false,
      },
      proposalDetailsTableHeaders: [
        { name: 'document', label: 'CPF' },
        { name: 'brand', label: 'Marca' },
        { name: 'model', label: 'Modelo' },
        { name: 'year', label: 'Ano do Modelo' },
        { name: 'license', label: 'Placa' },
        { name: 'external_proposal_number', label: 'CCB' },
      ],
      proposalDataTableHeaders: [
        { name: 'amount', label: 'Valor Financiado' },
        { name: 'installments', label: 'Parcelamento' },
        { name: 'supplier_name', label: 'Banco' },
      ],
      noStatusToShowCCBModal: ['Em análise', 'Aguardando formalização', 'Pendência', 'Recusada', 'Cancelada'],
    };
  },
  computed: {
    ...mapState({
      stateProposal: (state) => state.proposal.proposal,
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
      isInternalUser: 'user/isInternalUser',
    }),
    proposal_id() {
      return this.$route.params.proposal_id;
    },
    computedTitle() {
      return `Proposta # ${this.proposal_id}`;
    },
  },
  async mounted() {
    await this.getProposalDetails();
    if (this.proposalDetails.supplier_id !== config.SUPPLIER_ID_BV) {
      this.getInstallmentsByProposalId();
    }
  },
  methods: {
    ...mapActions({
      setConsult: 'consult/SET_CONSULT',
    }),
    // handlers
    handlerError(err) {
      let message = err?.response?.data?.error?.message
        || (err?.error?.response?.data?.error?.message
          || err?.error?.data?.message
          || err?.message
          || err);

      if (err?.response?.data?.error?.errors) {
        const errors = Object.values(err?.response?.data?.error?.errors);

        message = message.concat(`<br> - ${errors.map((error) => error).join('<br> - ')}`);
      }

      toast({
        message,
        type: 'is-danger',
        dismissible: true,
        pauseOnHover: true,
        animate: { in: 'fadeIn', out: 'fadeOut' },
        position: 'top-center',
        closeOnClick: true,
        duration: 10000,
      });
    },
    goBack() {
      this.$router.push('/cgv/propostas');
    },
    openInstallmentDetailModal(installment) {
      this.selectedInstallment = installment;
      this.modal.installmentDetail = true;
    },
    async goToUpdateProposalPage() {
      await this.setConsult(this.proposalDetails);
      this.$router.push(`/cgv/atualizar-proposta/${this.proposalDetails.proposal_id}`);
    },
    openModal(modalName, proposal) {
      /**
       * Se for o modal de detalhes do CCB e
       * o status da proposta for algum dos listados,
       * não irá abrir a modal.
       */
      if (
        modalName === 'ccbDetails' && this.noStatusToShowCCBModal.includes(proposal.status)
      ) {
        return;
      }

      if (proposal !== null) {
        if (proposal) this.proposalDetails = proposal;
      }
      this.modal[modalName] = true;
    },
    isSupplierBV(supplier) {
      return supplier === config.SUPPLIER_ID_BV;
    },

    // API
    async getProposalDetails() {
      this.loading = true;
      await CGVAPI.getProposalById(this.proposal_id)
        .then(({ data }) => {
          this.proposalDetails = data;
        })
        .catch(() => {

        })
        .finally(() => {
          this.loading = false;
        });
    },

    // API
    getInstallmentsByProposalId() {
      this.loading = true;
      CGVAPI.getInstallmentsByProposalId(this.proposal_id)
        .then(({ data }) => {
          this.installmentList = data;
        })
        .catch((err) => this.handlerError(err))
        .finally(() => {
          this.loading = false;
        });
    },
  },
  metaInfo() {
    return seo.meta(this.meta);
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_buttonsVariables.scss';
@import '@/assets/scss/src/_textVariables.scss';

.proposal-details {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  column-gap: 24px;

  grid-template-columns: 1fr;

  .first-column {
    display: flex;
    flex-direction: column;
    gap: 16px;

    &:not([has-installments]) {
      width: 100%;

      @media only screen and (min-width: 1024px) {
        flex-direction: row;
      }
    }
  }

  @media only screen and (min-width: 1024px) {
    row-gap: 24px;
    flex-direction: row;

    .first-column {
      gap: 24px;
      min-width: 350px;

      &:not([proposal-status="Paga"]) {
      width: 100%;
      flex-direction: row;
    }
    }
  }

  .proposal-details__vehicle-data,
  .proposal-details__details-data {
    width: 100%;
  }

  table {
    width: 100%;

    tr {
      padding: 12px 0;
    }

    th {
      color: map.get($stone-types, "500");
      width: 140px;
    }

    td {
      color: map.get($stone-types, "900");
    }

    tr:last-of-type {

      th,
      td {
        border-bottom: none;
      }
    }
  }

  .show__all__details__button {
    width: 100%;
  }

  .details-data__header {
    display: flex;
    justify-content: space-between;

    &-actions {
      display: flex;
      gap: 8px;

      button {
        width: 30px;
        height: 30px;
        padding: 2px;
      }
    }
  }

  &__installments-data {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;

    .installments-data__actions {
      display: flex;
      gap: 8px;
    }

    .table {
      width: 100%;
      border: 1px solid map.get($stone-types, "200");
      border-collapse: separate;
      border-radius: 16px;

      tr {

        &:nth-child(even) {
          background-color: map.get($stone-types, "50");
        }

        div {
          margin: 4px 16px;
          display: flex;
          justify-content: space-between;

          td {
            text-align: right;

            span {
              vertical-align: sub;
            }

            button {
              height: 30px;
              padding: 7px;

              &.icon-button {
                width: 30px;
                padding: 2px;
              }
            }
          }

          th,
          td {
            border-bottom: none;
            padding: 2px 0;
            line-height: 20px;
          }

          &:first-of-type {
            margin-top: 24px;
          }

          &:last-of-type {
            gap: 16px;
            justify-content: flex-start;
            margin-bottom: 24px;
          }
        }
      }
    }
  }

  .action__icon {
    stroke: none;
  }

  @media only screen and (min-width: 1024px) {
    .proposal-details {
      &__installments-data {

        .table {
          thead th {
            border-bottom: 1px solid map.get($stone-types, "200");
            background-color: map.get($stone-types, "50");
            text-transform: uppercase;
            letter-spacing: 0.05rem;
            text-align: center;

            &.left-aligned {
              text-align: left;
            }
          }

          td {
            border-bottom: none;
            padding: 18px 16px;
            color: map.get($stone-types, "500");

            span {
              vertical-align: sub;
            }

            button {
              height: 30px;
              padding: 7px;

              &.icon-button {
                width: 30px;
                padding: 2px;
              }
            }
          }
        }
      }
    }
  }
}

.ccb__proposal_number:not(.no_allowed_status) {
  text-decoration: underline;
  color: #4F46E5;
  cursor: pointer;
}

.no_allowed_status {
  font-weight: 600;
}
</style>
